import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { MessageService } from 'primeng/api';

import { RealtimeService } from './realtime.service';
import { RealtimeEvent, RealtimeEventType } from './realtime-event.types';

/**
 * Globale Toast-Brücke für Realtime-Events: subskribiert <c>RealtimeService.events$</c>
 * und übersetzt jedes Event in eine PrimeNG-Toast-Meldung. Dadurch sehen User
 * Live-Updates auch dann, wenn sie nicht auf der dazu passenden Liste sind.
 *
 * Listen-Refresh bleibt Aufgabe der Komponenten selbst — der Service hier
 * macht ausschließlich UI-Feedback. So bleiben die Verantwortlichkeiten klar
 * getrennt und ein vergessenes Toast in einem Refactor brockt nicht die
 * Datenaktualität.
 */
@Injectable({ providedIn: 'root' })
export class RealtimeToastService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly realtime = inject(RealtimeService);
  private readonly messages = inject(MessageService);

  constructor() {
    if (!isPlatformBrowser(this.platformId)) return;

    this.realtime.events$.subscribe((evt) => this.toast(evt));
  }

  private toast(evt: RealtimeEvent): void {
    const t = this.translate(evt);
    if (!t) return;

    this.messages.add({
      severity: t.severity,
      summary:  t.summary,
      detail:   t.detail,
      life:     6_000,
    });
  }

  private translate(evt: RealtimeEvent): { severity: 'info' | 'success' | 'warn'; summary: string; detail: string } | null {
    switch (evt.type) {
      case RealtimeEventType.AnfrageMatched:
        return {
          severity: 'info',
          summary:  'Neue Anfrage',
          detail:   'Du wurdest für eine neue Anfrage vorgeschlagen.',
        };
      case RealtimeEventType.AnfrageHandwerkerAssigned:
        return {
          severity: 'success',
          summary:  'Betrieb gefunden',
          detail:   'Wir haben einen passenden Betrieb für deine Anfrage gefunden.',
        };
      case RealtimeEventType.OfferSubmitted:
        return {
          severity: 'info',
          summary:  'Neues Angebot',
          detail:   'Ein Handwerker hat dir ein Angebot geschickt.',
        };
      case RealtimeEventType.OfferRejected:
        return {
          severity: 'warn',
          summary:  'Handwerker abgelehnt',
          detail:   'Ein Handwerker steht für deine Anfrage nicht mehr zur Verfügung.',
        };
      case RealtimeEventType.OfferAccepted:
        return {
          severity: 'success',
          summary:  'Angebot angenommen',
          detail:   'Der Kunde hat dein Angebot angenommen. Der Auftrag wartet im Cockpit.',
        };
      case RealtimeEventType.OfferPassedOver:
        return {
          severity: 'info',
          summary:  'Angebot nicht ausgewählt',
          detail:   'Der Kunde hat sich für einen anderen Handwerker entschieden.',
        };
      case RealtimeEventType.BookingStatusChanged:
        return {
          severity: 'info',
          summary:  'Auftrag aktualisiert',
          detail:   evt.payload.status
            ? `Neuer Status: ${this.humanStatus(evt.payload.status)}.`
            : 'Der Auftrag hat einen neuen Status.',
        };
      case RealtimeEventType.BookingReleased:
        return {
          severity: 'success',
          summary:  'Zahlung freigegeben',
          detail:   'Der Kunde hat den Auftrag bestätigt — die Auszahlung läuft.',
        };
      case RealtimeEventType.BookingDisputed:
        return {
          severity: 'warn',
          summary:  'Reklamation',
          detail:   'Der Kunde hat eine Reklamation eröffnet. Bitte im Cockpit prüfen.',
        };
      default:
        return null;
    }
  }

  private humanStatus(status: string): string {
    // Server schickt Enum-Strings wie "Confirmed"/"InProgress" — wir mappen
    // hier auf das, was im UI sonst auch steht. Unbekannte Werte bleiben
    // unverändert.
    switch (status) {
      case 'Pending':    return 'Wartet auf Zahlung';
      case 'Confirmed':  return 'Bestätigt';
      case 'InProgress': return 'In Arbeit';
      case 'Completed':  return 'Abgeschlossen';
      case 'Released':   return 'Freigegeben';
      case 'Disputed':   return 'Reklamation';
      case 'Cancelled':  return 'Storniert';
      case 'Refunded':   return 'Erstattet';
      default:           return status;
    }
  }
}
